import Assets from "./Assets/Assets";
import BankAndCashComponent from "./BankAndCashComponent/BankAndCashComponent";
import DonationAttributions from "./DonationAttributions/DonationAttributions";
import Expenses from "./Expenses/Expenses";
import IssuedClientInvoice from "./IssuedClientInvoice/IssuedClientInvoice";
import Loans from "./Loans/Loans";
import ReceivedVendorInvoice from "./ReceivedVendorInvoice/ReceivedVendorInvoice";
import Revenues from "./Revenues/Revenues";
import UsagesOfDonationFunds from "./UsagesOfDonationFunds/UsagesOfDonationFunds";
import { PropsFilter } from "../../../models/api/props-filter";
import { LegalEntityDonationBeneficiaryFilter } from "../../../models/legal-entity-donation-beneficiaries/legal-entity-donation-beneficiary-filter";
import { LegalEntityDonatorMemberFilter } from "../../../models/legal-entity-donators-members";
import { TransactionDTO } from "../../../models/transactions";
import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";

import { LegalEntityClientsApiService } from "../../../services/legal-entity-clients/legal-entity-clients-api.service";
import { LegalEntityDonationBeneficiariesApiService } from "../../../services/legal-entity-donation-beneficiaries/legal-entity-donation-beneficiaries-api.service";
import { LegalEntityDonatorsMembersApiService } from "../../../services/legal-entity-donators-members/legal-entity-donators-members-api.service";
import { LegalEntityVendorsApiService } from "../../../services/legal-entity-vendors/legal-entity-vendors-api.service";
import { LegislationsApiService } from "../../../services/legislations/legislations-api.service";
import { TransactionStatusesApiService } from "../../../services/transaction-statuses/transaction-statuses-api.service";

interface Props {
  transaction: TransactionDTO;
  dynamicField: string;
  firstPaymentAcc: string;
  bankAndCashAccounts: string[];
  handleChangeField: (field: keyof TransactionDTO, value: TransactionDTO[keyof TransactionDTO]) => void;
  defaultTransaction?: TransactionDTO | null;
}

const TransactionDynamicField = ({
  transaction,
  dynamicField,
  handleChangeField,
  firstPaymentAcc,
  bankAndCashAccounts,
  defaultTransaction,
}: Props) => {
  const tRP = "pages.system_settings.tables.transaction_types.fields_configs"; // tRP - translation root path

  const getDynamicFieldComponent = (dynamicField: string) => {
    switch (dynamicField) {
      case "Amount":
        return;

      case "Assets":
        return (
          <Assets
            transaction={transaction}
            handleChange={handleChangeField}
          />
        );

      case "Expenses":
        return (
          <Expenses
            transaction={transaction}
            handleChange={handleChangeField}
          />
        );

      case "Revenues":
        return (
          <Revenues
            transaction={transaction}
            handleChange={handleChangeField}
          />
        );

      case "AttributionsOfTheReceivedDonation":
        return (
          <DonationAttributions
            transaction={transaction}
            handleChange={handleChangeField}
          />
        );

      case "UsagesOfDonationFunds":
        return (
          <UsagesOfDonationFunds
            transaction={transaction}
            handleChange={handleChangeField}
          />
        );

      case "Loans":
        return (
          <Loans
            transaction={transaction}
            handleChange={handleChangeField}
          />
        );

      case "DonatorMember":
        return (
          <AutocompleteDropdown
            labelKey={`${tRP}.donatorMember`}
            field="donatorMember"
            serviceApi={new LegalEntityDonatorsMembersApiService()}
            handleChange={handleChangeField}
            propsItem={transaction}
            sxProps={{ maxWidth: "300px" }}
            propsFilter={
              {
                legalEntityIds: transaction.legalEntity?.id ? [transaction.legalEntity?.id] : [],
              } as LegalEntityDonatorMemberFilter
            }
          />
        );

      case "DonationBeneficiary":
        return (
          <AutocompleteDropdown
            labelKey={`${tRP}.donationBeneficiary`}
            field="donationBeneficiary"
            serviceApi={new LegalEntityDonationBeneficiariesApiService()}
            handleChange={handleChangeField}
            propsItem={transaction}
            sxProps={{ maxWidth: "300px" }}
            propsFilter={
              {
                legalEntityIds: transaction.legalEntity?.id ? [transaction.legalEntity?.id] : [],
              } as LegalEntityDonationBeneficiaryFilter
            }
          />
        );

      case "Client":
        return (
          <AutocompleteDropdown
            labelKey={`${tRP}.client`}
            field="client"
            serviceApi={new LegalEntityClientsApiService()}
            handleChange={handleChangeField}
            propsItem={transaction}
            sxProps={{ maxWidth: "300px" }}
            propsFilter={
              {
                legalEntityIds: transaction.legalEntity?.id ? [transaction.legalEntity?.id] : [],
              } as PropsFilter
            }
          />
        );

      case "ClientInvoice":
        return (
          <IssuedClientInvoice
            transaction={transaction}
            handleChange={handleChangeField}
            defaultTransaction={defaultTransaction}
          />
        );

      case firstPaymentAcc:
        return (
          <BankAndCashComponent
            handleChange={handleChangeField}
            bankAndCashAccounts={bankAndCashAccounts}
            transaction={transaction}
          />
        );

      case "FromWhichBankAccount":
        return;

      case "FromWhichCashAccount":
        return;

      case "ToWhichCashAccount":
        return;

      case "Legislation":
        return (
          <AutocompleteDropdown
            labelKey={"common_words.legislation"}
            field="legislation"
            serviceApi={new LegislationsApiService()}
            handleChange={handleChangeField}
            sxProps={{ maxWidth: "300px" }}
          />
        );

      case "Status":
        return (
          <AutocompleteDropdown
            labelKey={`${tRP}.status`}
            field="status"
            serviceApi={new TransactionStatusesApiService()}
            handleChange={handleChangeField}
            propsItem={transaction}
            sxProps={{ maxWidth: "300px" }}
          />
        );

      case "ToWhichBankAccount":
        return;

      case "Vendor":
        return (
          <AutocompleteDropdown
            labelKey={`${tRP}.vendor`}
            field="vendor"
            serviceApi={new LegalEntityVendorsApiService()}
            handleChange={handleChangeField}
            propsItem={transaction}
            sxProps={{ maxWidth: "300px" }}
            propsFilter={
              {
                legalEntityIds: transaction.legalEntity?.id ? [transaction.legalEntity?.id] : [],
              } as PropsFilter
            }
          />
        );

      case "VendorInvoice":
        return (
          <ReceivedVendorInvoice
            transaction={transaction}
            handleChange={handleChangeField}
            defaultTransaction={defaultTransaction}
          />
        );
      default:
        return null;
    }
  };

  return <>{getDynamicFieldComponent(dynamicField)}</>;
};

export default TransactionDynamicField;
